import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onboardingPath, OnboardingFlowMode } from './onboardingRoutes';

interface OnboardingCompatRedirectProps {
  /**
   * When set, the legacy route was already unambiguously this mode —
   * redirect straight to `/onboarding/{fixedMode}/{step}` with no lookup.
   */
  fixedMode?: OnboardingFlowMode;
  /** The backend step to route to under the resolved mode. */
  step: string;
}

/**
 * R7 compatibility redirect for the pre-split flat onboarding routes
 * (`/onboarding/outcomes`, `/onboarding/setup`, …).
 *
 * - `fixedMode` set → redirect straight to the mode-specific route.
 * - `fixedMode` unset → resolve the mode from onboarding state (a plain
 *   GET — never create-workspace or any other side effect) and redirect
 *   to the mode-specific route. If the mode cannot be determined, recover
 *   safely to `/onboarding/new` (the workspace chooser).
 *
 * Any `?continue=` param (OIDC / suite-app handoff) is preserved.
 */
export function OnboardingCompatRedirect({ fixedMode, step }: OnboardingCompatRedirectProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || undefined;

  useEffect(() => {
    function recoverToNew() {
      navigate(
        continueUrl
          ? `/onboarding/new?continue=${encodeURIComponent(continueUrl)}`
          : '/onboarding/new',
        { replace: true },
      );
    }

    if (fixedMode) {
      navigate(onboardingPath(fixedMode, step, continueUrl), { replace: true });
      return;
    }

    // Mode unknown — resolve it from onboarding state. GET only: this must
    // never create a workspace or mutate anything.
    let cancelled = false;
    fetch('/api/onboarding/state', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const mode = data?.state?.onboardingMode;
        if (mode === 'business' || mode === 'personal') {
          navigate(onboardingPath(mode, step, continueUrl), { replace: true });
        } else {
          recoverToNew();
        }
      })
      .catch(() => {
        if (!cancelled) recoverToNew();
      });

    return () => {
      cancelled = true;
    };
  }, [fixedMode, step, continueUrl, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div
        role="status"
        aria-label="Redirecting"
        className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"
      />
    </div>
  );
}
